import React, { useEffect, useRef, useState } from 'react';
import type { ProductDto } from '../types/product';
import { useCreateProduct, useUpdateProduct } from '../hooks/useProductMutations';
import { API_BASE, BACKEND_URL } from '../config';
import '../styles/Modal.css';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: ProductDto | null;
  categoryId: number;
}

interface FormState {
  nameHebrew: string;
  nameEnglish: string;
  weight: string;
  manufacturer: string;
  kashrut: string;
  imageUrl: string;
  price: string;
}

interface FormErrors {
  nameHebrew?: string;
  nameEnglish?: string;
  weight?: string;
  manufacturer?: string;
  kashrut?: string;
  price?: string;
}

const DEFAULT_FORM: FormState = {
  nameHebrew: '',
  nameEnglish: '',
  weight: '',
  manufacturer: '',
  kashrut: '',
  imageUrl: '',
  price: '',
};

export function ProductFormModal({
  isOpen,
  onClose,
  product,
  categoryId,
}: ProductFormModalProps): JSX.Element | null {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();

  const isEdit = product != null;
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (isOpen) {
      if (product) {
        setForm({
          nameHebrew: product.nameHebrew,
          nameEnglish: product.nameEnglish ?? '',
          weight: String(product.weight),
          manufacturer: product.manufacturer,
          kashrut: product.kashrut,
          imageUrl: product.imageUrl ?? '',
          price: product.price != null ? String(product.price) : '',
        });
      } else {
        setForm(DEFAULT_FORM);
      }
      setErrors({});
      setApiError(null);
    }
  }, [isOpen, product]);

  if (!isOpen) return null;

  function validate(): boolean {
    const newErrors: FormErrors = {};
    if (!form.nameHebrew.trim()) newErrors.nameHebrew = 'שדה חובה';
    if (!form.manufacturer.trim()) newErrors.manufacturer = 'שדה חובה';
    if (!form.kashrut.trim()) newErrors.kashrut = 'שדה חובה';
    const weightNum = parseFloat(form.weight);
    if (!form.weight || isNaN(weightNum) || weightNum <= 0) {
      newErrors.weight = 'משקל חייב להיות גדול מ-0';
    }
    if (form.price !== '') {
      const priceNum = parseFloat(form.price);
      if (isNaN(priceNum) || priceNum < 0) {
        newErrors.price = 'מחיר חייב להיות 0 או יותר';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleImageFile(file: File): Promise<void> {
    setImageError(null);
    setImageUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/products/image`, { method: 'POST', body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { detail?: string }).detail ?? 'שגיאה בהעלאת תמונה');
      }
      const data = await res.json() as { iconUrl: string };
      setForm((prev) => ({ ...prev, imageUrl: data.iconUrl }));
    } catch (err) {
      setImageError(err instanceof Error ? err.message : 'שגיאה בהעלאת תמונה');
    } finally {
      setImageUploading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!validate()) return;
    setApiError(null);

    const payload = {
      nameHebrew: form.nameHebrew.trim(),
      nameEnglish: form.nameEnglish.trim() || null,
      weight: parseFloat(form.weight),
      manufacturer: form.manufacturer.trim(),
      kashrut: form.kashrut,
      imageUrl: form.imageUrl.trim() || null,
      price: form.price !== '' ? parseFloat(form.price) : null,
      categoryId,
    };

    try {
      if (isEdit && product) {
        await updateMutation.mutateAsync({ id: product.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'שגיאה בשמירה');
    }
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement>
  ): void {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-box" dir="rtl">
        <h2 className="modal-title">{isEdit ? 'עריכת מוצר' : 'הוספת מוצר'}</h2>
        {apiError && <div className="api-error-message">{apiError}</div>}

        <form className="modal-form" onSubmit={handleSubmit} noValidate>
          <div className="form-field">
            <label className="form-label" htmlFor="nameHebrew">
              שם מוצר (עברית) *
            </label>
            <input
              id="nameHebrew"
              name="nameHebrew"
              type="text"
              className={`form-input${errors.nameHebrew ? ' form-input--error' : ''}`}
              value={form.nameHebrew}
              onChange={handleChange}
            />
            {errors.nameHebrew && (
              <span className="form-error">{errors.nameHebrew}</span>
            )}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="nameEnglish">
              שם מוצר (אנגלית)
            </label>
            <input
              id="nameEnglish"
              name="nameEnglish"
              type="text"
              dir="ltr"
              className={`form-input${errors.nameEnglish ? ' form-input--error' : ''}`}
              value={form.nameEnglish}
              onChange={handleChange}
            />
            {errors.nameEnglish && (
              <span className="form-error">{errors.nameEnglish}</span>
            )}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="weight">
              משקל (ק״ג) *
            </label>
            <input
              id="weight"
              name="weight"
              type="number"
              step="0.001"
              min="0.001"
              className={`form-input${errors.weight ? ' form-input--error' : ''}`}
              value={form.weight}
              onChange={handleChange}
            />
            {errors.weight && (
              <span className="form-error">{errors.weight}</span>
            )}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="manufacturer">
              יצרן *
            </label>
            <input
              id="manufacturer"
              name="manufacturer"
              type="text"
              className={`form-input${errors.manufacturer ? ' form-input--error' : ''}`}
              value={form.manufacturer}
              onChange={handleChange}
            />
            {errors.manufacturer && (
              <span className="form-error">{errors.manufacturer}</span>
            )}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="kashrut">
              כשרות *
            </label>
            <input
              id="kashrut"
              name="kashrut"
              type="text"
              className={`form-input${errors.kashrut ? ' form-input--error' : ''}`}
              value={form.kashrut}
              onChange={handleChange}
              placeholder="למשל: פרווה, חלבי, בשרי..."
            />
            {errors.kashrut && (
              <span className="form-error">{errors.kashrut}</span>
            )}
          </div>

          <div className="form-field">
            <label className="form-label">תמונה</label>

            {/* Preview */}
            {form.imageUrl && (
              <div className="image-upload-preview">
                <img
                  src={form.imageUrl.startsWith('http') ? form.imageUrl : BACKEND_URL + form.imageUrl}
                  alt="תצוגה מקדימה"
                  className="icon-preview"
                  style={{ width: 80, height: 80, objectFit: 'contain' }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                  onClick={() => setForm((prev) => ({ ...prev, imageUrl: '' }))}
                >
                  הסר
                </button>
              </div>
            )}

            {/* Upload button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageFile(file);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageUploading}
              style={{ width: '100%', marginTop: form.imageUrl ? '0.4rem' : 0 }}
            >
              {imageUploading ? '⏳ מעלה...' : form.imageUrl ? '🔄 החלף תמונה' : '📁 בחר תמונה'}
            </button>
            {imageError && <span className="form-error">{imageError}</span>}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="price">
              מחיר (₪)
            </label>
            <input
              id="price"
              name="price"
              type="number"
              step="0.01"
              min="0"
              className={`form-input${errors.price ? ' form-input--error' : ''}`}
              value={form.price}
              onChange={handleChange}
              placeholder="0.00"
            />
            {errors.price && (
              <span className="form-error">{errors.price}</span>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isPending}
            >
              ביטול
            </button>
            <button type="submit" className="btn-primary" disabled={isPending}>
              {isPending ? 'שומר...' : isEdit ? 'עדכן' : 'הוסף'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
